/**
 * Pure sidebar utilities — conversation ordering, container display naming, and
 * context-menu position clamping.
 */

import type { Conversation } from '@/types/conversations';
import { isConversationPinned } from '@/types/conversations';
import type { ContainerAccess } from '@/types/session';

export type ConversationBucket = 'pinned' | 'unpinned';

export function orderConversationsWithPinnedFirst(conversations: Conversation[]): Conversation[] {
  const pinned: Conversation[] = [];
  const unpinned: Conversation[] = [];

  for (const conversation of conversations) {
    if (isConversationPinned(conversation)) {
      pinned.push(conversation);
      continue;
    }
    unpinned.push(conversation);
  }

  return [...pinned, ...unpinned];
}

export function buildPinnedConversationOrder(
  conversations: Conversation[],
  options?: {
    targetConversationId?: string;
    placeTargetAtTopOf?: ConversationBucket;
  },
): string[] {
  const pinned: Conversation[] = [];
  const unpinned: Conversation[] = [];

  for (const conversation of conversations) {
    if (!conversation?.id) continue;
    if (isConversationPinned(conversation)) {
      pinned.push(conversation);
      continue;
    }
    unpinned.push(conversation);
  }

  const targetConversationId = options?.targetConversationId;
  const placeTargetAtTopOf = options?.placeTargetAtTopOf;
  if (targetConversationId && placeTargetAtTopOf) {
    const bucket = placeTargetAtTopOf === 'pinned' ? pinned : unpinned;
    const targetIndex = bucket.findIndex((conversation) => conversation.id === targetConversationId);
    if (targetIndex > 0) {
      const [targetConversation] = bucket.splice(targetIndex, 1);
      bucket.unshift(targetConversation);
    }
  }

  return [...pinned, ...unpinned].map((conversation) => conversation.id);
}

export function getContainerDisplayName(container: ContainerAccess): string {
  if (container.containerName) {
    return container.containerName;
  }
  if (container.isOwner) {
    return 'My Workspace';
  }
  return container.ownerName || container.ownerEmail?.split('@')[0] || 'Shared Space';
}

/**
 * Stable React list key for a workspace container (#5542). `id` is the normal
 * case. Some shared/team containers arrive with a null/empty `id` (the raw
 * `GET /auth/containers` payload is mapped through as `any`, so the `string`
 * type on `ContainerAccess.id` isn't actually guaranteed at runtime) — the
 * previous `container.id || container.ownerId` fallback collided whenever 2+
 * such containers shared an `ownerId`. Prefers a real discriminator
 * (`containerName`, else `ownerEmail`) for readability, but `ownerEmail`
 * describes the OWNER, not the container — two null-id, unnamed containers
 * from the SAME owner still resolve to the identical discriminator and would
 * collide again. The `index` is therefore always appended as a final
 * tiebreaker for null-id containers (guaranteed unique within one render of
 * the list); this trades key stability across reorders for correctness,
 * which is the right call for what is already a degenerate, rare case.
 */
export function getContainerKey(container: ContainerAccess, index: number): string {
  if (container.id) return container.id;
  const discriminator = container.containerName || container.ownerEmail || 'shared';
  return `owner:${container.ownerId}:${discriminator}:${index}`;
}

/**
 * Clamp a context-menu position so the menu stays fully on screen.
 *
 * Mirrors the inline clamp formula used at the four context-menu sites
 * (conversation context-menu / actions-click 190x196, module context-menu /
 * actions-click 190x124). The mobile module long-press site uses a different
 * center-of-screen formula and is intentionally NOT routed through this util.
 */
export function clampMenuPosition({
  x,
  y,
  width,
  height,
  padding,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  padding: number;
}): { x: number; y: number } {
  const clampedX = Math.max(
    padding,
    Math.min(x, window.innerWidth - width - padding),
  );
  const clampedY = Math.max(
    padding,
    Math.min(y, window.innerHeight - height - padding),
  );
  return { x: clampedX, y: clampedY };
}
